import React, { ReactNode, useEffect, useState } from 'react'
import { reactular } from '@vendhq/reactular'
import { SelectedTabProvider, Tab, Tabs } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import {
  selectCustomFields,
  selectSaleHasLineItemFulfilment,
  selectServiceFields,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useVdFeatureManager } from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { hasServiceOrder } from '../../../functions/service'

export enum LineItemTab {
  DETAIL = 'detail',
  FULFILMENT = 'fulfilment',
}

interface LineItemTabsProps {
  defaultTab?: LineItemTab
  onChange: (selected: LineItemTab) => void
  children?: ReactNode | undefined
}

export const LineItemTabs: React.FC<LineItemTabsProps> = ({
  defaultTab = LineItemTab.DETAIL,
  onChange,
  children,
}) => {
  const t = useT()
  const [currentTab, setCurrentTab] = useState(defaultTab)
  const vdFeatureManager = useVdFeatureManager()
  const isFulfilmentLifEnabled = vdFeatureManager.get('fulfillment_lif')
  const isFulfilmentLifEnabledB2 = vdFeatureManager.get('fulfillment_lif_b3')
  const isReactSaleSummaryEnabled = vdFeatureManager.get('react_sale_summary')
  const hasFulfilment = useAppSelector(selectSaleHasLineItemFulfilment)
  const serviceFields = useAppSelector(selectServiceFields)
  const customFields = useAppSelector(selectCustomFields)
  const isDisplay = isFulfilmentLifEnabled && hasFulfilment

  useEffect(() => {
    if (!isDisplay && currentTab === LineItemTab.FULFILMENT) {
      setCurrentTab(LineItemTab.DETAIL)
    }
  }, [isDisplay, currentTab])

  const displayTabs =
    (isFulfilmentLifEnabled && isFulfilmentLifEnabledB2) || isDisplay

  const hasShowServicePicklist = vdFeatureManager.get('show_service_pick_lists')

  const isServiceSale =
    hasShowServicePicklist && hasServiceOrder(serviceFields, customFields)

  // We want to hide fulfilment tab if show_service_pick_lists FF is on and sale is a service
  const shouldHideFulfilmentTab = hasShowServicePicklist && isServiceSale
  if (!displayTabs) {
    return null
  }

  return (
    <SelectedTabProvider
      defaultTab={defaultTab}
      onChange={string => {
        const selectedTab = string as LineItemTab
        setCurrentTab(selectedTab)
        onChange(selectedTab)
      }}
    >
      {isReactSaleSummaryEnabled ? (
        <Tabs className="vd-mb3">
          <Tab name={LineItemTab.DETAIL}>
            {t('Details', { _context: 'Line item tabs' })}
          </Tab>
          {displayTabs && !shouldHideFulfilmentTab && (
            <Tab name={LineItemTab.FULFILMENT}>
              {t('Fulfillment', { _context: 'Line item tabs' })}
            </Tab>
          )}
        </Tabs>
      ) : (
        <Tabs>
          <Tab name={LineItemTab.DETAIL}>
            {t('Details', { _context: 'Line item tabs' })}
          </Tab>
          {displayTabs && !shouldHideFulfilmentTab && (
            <Tab name={LineItemTab.FULFILMENT}>
              {t('Fulfillment', { _context: 'Line item tabs' })}
            </Tab>
          )}
        </Tabs>
      )}

      {children}
    </SelectedTabProvider>
  )
}

export const LineItemTabsComponent = reactular(
  LineItemTabs,
  ['defaultTab', 'onChange'],
  'reactWrapper'
)
